/**
 * Analysis Helpers - Shared utilities for analysis processing
 *
 * DRY: These functions are used by:
 * - workflows-api (analyze worker)
 * - edge-api (prompt builder preview)
 * - ui-worker (mappers)
 *
 * All analysis output processing should go through these shared functions.
 */

import { transformKeysToCamel } from './case-transform';
import { recommendationParts, withoutFinalStop } from './sentence-end';
import { readObjectionScore, reconcileObjectionHandling } from './objection-handling';
import {
  QUALITY_SUB_METRIC_KEYS,
  computeCanonicalQualityScore,
  isCountedSubMetric,
} from './quality-scale';
import type { SpeakerRole } from '../types/analysis-evidence';
import type { ObjectionCode } from '../voc/objection-codes';

/**
 * Structured output from PROMPT_BUILDER_SCHEMA (snake_case from LLM)
 */
export interface AnalysisStructuredOutput {
  quality_score?: number | null;
  sale_probability?: number | null;
  attention_flag?: {
    triggered: boolean;
    severity: 'critical' | 'high' | 'medium' | 'low';
    reason: string;
  } | null;
  topic?: string | null;
  keywords?: string[] | null;
  key_moments?: Array<{
    timestamp: string | null;
    label: string;
    quote: string;
    importance: 'critical' | 'high' | 'medium' | 'low';
    speaker: 'agent' | 'customer' | 'unknown';
    supports_attention_flag?: boolean;
  }> | null;
  summary?: string | null;
  structured_summary?: {
    situation: string;
    actions: string[];
    outcome: string;
  } | null;
  filler_words?: {
    count: number;
    examples: string[];
  } | null;
  stop_words?: {
    // New grounded shape is an array of {token, speaker, quote}; older stored
    // analyses used a flat string[]. Accept both for backward compatibility.
    detected: Array<string | { token: string; speaker?: string; quote?: string }>;
  } | null;
  mandatory_phrases?: {
    required: string[];
    found: string[];
    missing: string[];
    // Optional: absent on analyses stored before Task 3 (analysis-metric-
    // semantics) added this field. New analyses always populate it (schema-
    // required whenever mandatory_phrases itself is present).
    missing_codes?: string[];
  } | null;
  profanity_detection?: {
    detected: boolean;
    instances: Array<{
      speaker: 'agent' | 'client' | 'unknown';
      phrase: string;
      timestamp: string;
    }>;
  } | null;
  operator_errors?: Array<{
    type: string;
    description: string;
    /** #981 — see `operatorErrors` in the camelCase output below. */
    kind?: 'said' | 'omitted';
    evidence_quote?: string | null;
  }> | null;
  greeting_intro?: {
    score: number;
    feedback: string;
  } | null;
  needs_qualification?: {
    score: number;
    identified_needs: string[];
  } | null;
  objection_handling?: {
    objections: Array<{
      type: string;
      score: number;
      quote: string;
      code: ObjectionCode;
      secondary_code: ObjectionCode | null;
    }>;
  } | null;
  closing_techniques?: {
    score: number;
    techniques_used: string[];
  } | null;
  sentiment?: {
    // Client sentiment only (snake_case from LLM). Agent (employee) emotion is
    // not scored — EU AI Act Art. 5(1)(f).
    client_start?: number; // -1.0 to +1.0
    client_end?: number; // -1.0 to +1.0
    delta?: number; // client_end - client_start
    overall?: number | 'positive' | 'neutral' | 'negative'; // number or legacy text
    // Legacy text format (for backward compatibility)
    customer?: 'positive' | 'neutral' | 'negative';
  } | null;
  politeness?: {
    score: number;
    polite_phrases: string[];
  } | null;
  interruptions?: {
    count: number;
    by_agent: number;
    by_customer: number;
  } | null;
  clarity?: {
    score: number;
    feedback: string;
  } | null;
  recording_disclaimer?: {
    mentioned: boolean;
    timestamp: string | null;
  } | null;
  crm_completeness?: {
    /** False when collecting the customer's details was not this call's task. */
    applicable?: boolean;
    /** Null when not applicable — the metric is skipped, not scored zero. */
    score: number | null;
    missing_fields: string[];
  } | null;
  strengths_improvements?: {
    strengths: Array<{
      behavior: string;
      impact: string;
    }>;
    improvements: Array<{
      behavior: string;
      suggestion: string;
    }>;
  } | null;
  business_intelligence?: {
    competitor_mentions: Array<{
      name: string;
      context: string;
      sentiment: 'positive' | 'neutral' | 'negative';
    }>;
    product_feedback: Array<{
      product: string;
      feedback: string;
      severity: 'praise' | 'minor_issue' | 'major_issue' | 'incident';
    }>;
    market_signals: Array<{
      signal: string;
      type: 'pricing' | 'supply' | 'regulation' | 'trend' | 'demand';
    }>;
    compliance_flags: Array<{
      flag: string;
      context: string;
    }>;
  } | null;
  extracted_crm_fields?: Array<{
    field_id: string;
    value: string;
    confidence: number;
    evidence_quote: string;
  }> | null;
  agreements?: Array<{
    type: 'call' | 'meeting' | 'email' | 'task';
    subject: string;
    description: string;
    due_hint: string;
    due_at: string;
    responsible_hint: string;
    confidence: number;
    evidence_quote: string;
    /** Transcript timecode (MM:SS / HH:MM:SS) of `evidence_quote`. */
    evidence_timestamp: string;
  }> | null;
}

/**
 * Transformed output in camelCase (for API responses and UI)
 * Index signature allows use as Record<string, unknown>
 */
export interface AnalysisOutputCamelCase {
  [key: string]: unknown;
  /**
   * Pipeline provenance the analysis carries about itself. Only the fields a
   * consumer of this transform actually reads are typed here; the rest stay
   * under the index signature.
   */
  analysisMetadata?: {
    /**
     * Conversation role per ASR speaker label, resolved by categorize-step
     * against grounded evidence — NOT re-derivable from the label text, which
     * is `speaker_0`/`speaker_1` whenever transcript cleanup was rejected.
     */
    speakerRoles?: Record<string, SpeakerRole>;
  } | null;
  qualityScore?: number | null;
  saleProbability?: number | null;
  attentionFlag?: {
    triggered: boolean;
    severity: 'critical' | 'high' | 'medium' | 'low';
    reason: string;
  } | null;
  topic?: string | null;
  keywords?: string[] | null;
  keyMoments?: Array<{
    timestamp: string | null;
    label: string;
    quote: string;
    importance: 'critical' | 'high' | 'medium' | 'low';
    speaker: 'agent' | 'customer' | 'unknown';
    supportsAttentionFlag?: boolean;
  }> | null;
  summary?: string | null;
  structuredSummary?: {
    situation: string;
    actions: string[];
    outcome: string;
  } | null;
  fillerWords?: {
    count: number;
    examples: string[];
    /**
     * Звуков в минуту разговора. Пишется детерминированным счётом (#819); у
     * анализов до него поля нет, и их балл считается по-старому.
     */
    perMinute?: number;
  } | null;
  stopWords?: {
    detected: Array<string | { token: string; speaker?: string; quote?: string }>;
  } | null;
  mandatoryPhrases?: {
    required: string[];
    found: string[];
    missing: string[];
    // Optional: absent on analyses stored before Task 3 (analysis-metric-
    // semantics) added this field.
    missingCodes?: string[];
  } | null;
  profanityDetection?: {
    detected: boolean;
    instances: Array<{
      speaker: 'agent' | 'client' | 'unknown';
      phrase: string;
      timestamp: string;
    }>;
  } | null;
  operatorErrors?: Array<{
    type: string;
    description: string;
    /**
     * #981: `said` — backed by the agent's own line; `omitted` — backed by the
     * line that called for the action. Optional: analyses stored before the
     * field existed carry neither.
     */
    kind?: 'said' | 'omitted';
    evidenceQuote?: string | null;
  }> | null;
  greetingIntro?: {
    score: number;
    feedback: string;
  } | null;
  needsQualification?: {
    // null when NO rubric component could apply to this call (consumer buying
    // at a published price: nothing to budget, the customer IS the decision
    // maker) — qualification was not this call's job, which is not a 0.
    score: number | null;
    identifiedNeeds: string[];
  } | null;
  objectionHandling?: {
    score: number;
    objections: Array<{
      type: string;
      handled: boolean;
      /** Absent on analyses persisted before objection scoring was introduced. */
      score?: number;
      quote: string;
      /** Absent on analyses persisted before canonical codes (#842). */
      code?: ObjectionCode;
      secondaryCode?: ObjectionCode | null;
    }>;
  } | null;
  closingTechniques?: {
    // null when closing was never applicable to this call (stale/wrong
    // contact, customer disengaged during qualification, disconnected
    // early) — not a failed close, which scores 0.
    score: number | null;
    techniquesUsed: string[];
  } | null;
  sentiment?: {
    // Client sentiment only (matches @aica/core Sentiment interface). Agent
    // (employee) emotion is not scored — EU AI Act Art. 5(1)(f).
    clientStart?: number; // -1.0 to +1.0
    clientEnd?: number; // -1.0 to +1.0
    delta?: number; // clientEnd - clientStart
    overall?: number | 'positive' | 'neutral' | 'negative'; // number or legacy text
    // Legacy text format (for backward compatibility)
    customer?: 'positive' | 'neutral' | 'negative';
  } | null;
  politeness?: {
    score: number;
    politePhrases: string[];
  } | null;
  interruptions?: {
    count: number;
    byAgent: number;
    byCustomer: number;
  } | null;
  clarity?: {
    score: number;
    feedback: string;
  } | null;
  recordingDisclaimer?: {
    mentioned: boolean;
    timestamp: string | null;
  } | null;
  /**
   * Structured per-call compliance verdicts — the shape
   * `applyComplianceApplicability` produced, camelCased. Analyses stored since
   * stage E3 (#2846) have no such field: the built-in criteria answer it. This is the source of truth for
   * recordingDisclaimer/tcpaCompliance/dataPrivacy pass/fail; the legacy
   * `recordingDisclaimer` field above still carries `mentioned`/`timestamp`
   * alongside it (same LLM call, both fields present) and is the ONLY source
   * for analyses stored before this field existed.
   */
  compliance?: {
    recordingDisclaimer?: {
      applicable: boolean;
      passed: boolean | null;
      quote: string;
    } | null;
    tcpaCompliance?: {
      applicable: boolean;
      passed: boolean | null;
      checklist: {
        companyIdentified: boolean;
        purposeStated: boolean;
        optOutOffered: boolean;
      } | null;
      reason: string;
    } | null;
    dataPrivacy?: {
      applicable: boolean;
      passed: boolean | null;
      quote: string;
    } | null;
  } | null;
  crmCompleteness?: {
    applicable?: boolean;
    score: number | null;
    missingFields: string[];
  } | null;
  strengthsImprovements?: {
    strengths: Array<{
      behavior: string;
      impact: string;
    }>;
    improvements: Array<{
      behavior: string;
      suggestion: string;
    }>;
  } | null;
  businessIntelligence?: {
    competitorMentions: Array<{
      name: string;
      context: string;
      sentiment: 'positive' | 'neutral' | 'negative';
    }>;
    productFeedback: Array<{
      product: string;
      feedback: string;
      severity: 'praise' | 'minor_issue' | 'major_issue' | 'incident';
    }>;
    marketSignals: Array<{
      signal: string;
      type: 'pricing' | 'supply' | 'regulation' | 'trend' | 'demand';
    }>;
    complianceFlags: Array<{
      flag: string;
      context: string;
    }>;
  } | null;
  extractedCrmFields?: Array<{
    fieldId: string;
    value: string;
    confidence: number;
    evidenceQuote: string;
  }> | null;
  agreements?: Array<{
    type: 'call' | 'meeting' | 'email' | 'task';
    subject: string;
    description: string;
    dueHint: string;
    dueAt: string;
    responsibleHint: string;
    confidence: number;
    evidenceQuote: string;
    /** Transcript timecode (MM:SS / HH:MM:SS) of `evidenceQuote`. */
    evidenceTimestamp: string;
  }> | null;
}

/**
 * Extracted metrics for storage in D1 (compact numeric values)
 * Uses Record<string, number> for compatibility with storage layer
 */
export type AnalysisMetrics = Record<string, number>;

/**
 * Aggregate/primary metrics - shown separately in summary section
 * These are calculated from component scores and represent overall performance
 */
export const AGGREGATE_METRICS = ['qualityScore', 'saleProbability'] as const;

/**
 * Component/detailed metrics - shown as colored progress bars
 * These are individual skill scores that make up the aggregate scores
 */
export const COMPONENT_METRICS = [
  'greetingScore',
  'needsScore',
  'objectionScore',
  'closingScore',
  'politenessScore',
  'clarityScore',
  'crmScore',
] as const;

/**
 * The exact set of keys `extractAnalysisMetrics` writes into `analyses.metrics`.
 *
 * SSOT for the *writer* side of the metrics contract: every key a repository SQL
 * aggregate extracts from `analyses.metrics` must appear here, i.e. must have a
 * writer. A reader without a writer is the "Customer Satisfaction" bug class — a
 * dashboard aggregate that averages a JSON path the pipeline never emits, so it
 * silently reads all-NULL. The `writer-coverage` contract test
 * (packages/database) pins the two sides together and also re-runs
 * `extractAnalysisMetrics` over a fully-populated input to prove this list stays
 * identical to what the emitter actually produces — add an emitted key and
 * forget this list, the test fails.
 *
 * Derived from the two existing constants rather than re-listed, so there is one
 * place to change: `AGGREGATE_METRICS` (qualityScore, saleProbability) plus
 * `COMPONENT_METRICS` (the seven sub-metrics).
 */
export const EMITTED_METRIC_KEYS = [...AGGREGATE_METRICS, ...COMPONENT_METRICS] as const;

/**
 * Check if a metric key is an aggregate metric
 */
export function isAggregateMetric(key: string): boolean {
  return (AGGREGATE_METRICS as readonly string[]).includes(key);
}

/**
 * Check if a metric key is a rated component of the quality score.
 *
 * Derived from `QUALITY_SUB_METRIC_KEYS`, NOT from `COMPONENT_METRICS`: the two
 * sets diverged when `crmScore` left the rating. `COMPONENT_METRICS` answers
 * "does the emitter write this key" (crmScore: yes, it is still stored and shown
 * as its own CRM-completeness indicator); this answers "is this one of the skills
 * the quality score averages" (crmScore: no). UI score breakdowns filter on this,
 * so keying it off the emitter set would render crmScore as a nameless bar.
 */
export function isComponentMetric(key: string): boolean {
  return (QUALITY_SUB_METRIC_KEYS as readonly string[]).includes(key);
}

/**
 * Sale probability as the model is contracted to answer it (#648): a FRACTION
 * from 0 to 1. The response schema bounds `sale_probability` to 0-1 and strict
 * Structured Outputs enforce the bound; OpenAI is the only analysis provider
 * (OpenRouter was removed 2026-08-11), so nothing can legitimately send more.
 *
 * The scale is never inferred from the size of the number. The write path used
 * to read "<= 1" as a fraction and anything larger as percent, which stored a
 * 1 % answer as 100 %; re-scaling 87 into 0.87 is the same guess from the other
 * side. A value outside the contract — above 1, below 0, a string, anything
 * but a finite number — is a violation and is dropped (null), so no metric,
 * CRM field or report states a number the model did not mean. Within the
 * contract, 1 means certainty.
 *
 * One rule for the finalizer and the stored metric, so both layers agree.
 */
export function saleProbabilityFraction(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1
    ? value
    : null;
}

/**
 * Parse a value that is supposed to be a real numeric metric, or return null.
 *
 * Unlike a bare `Number()`, this does NOT treat `''`, `[]`, `false`, `{}`
 * (all of which coerce to a finite 0) as a number — those are absent/non-
 * applicable values that must be omitted, not stored as a phantom `0` that
 * pollutes AVG()s and fires false "Low score: 0/100" notifications. Accepts an
 * actual finite number, or a non-empty string that parses to one.
 */
function asRealNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * A sub-metric of the quality score as the writer stores it: a real number
 * (`asRealNumber`) that every reader also counts (`isCountedSubMetric`). A
 * number beyond `QUALITY_SUB_METRIC_LIMIT` — no rubric produces one — is not
 * stored, so it never reaches a reader that would have to skip it (#1889).
 */
function asSubMetric(value: unknown): number | null {
  const number = asRealNumber(value);
  return isCountedSubMetric(number) ? number : null;
}

/**
 * Extract metrics from analysis structured output (PROMPT_BUILDER_SCHEMA format)
 *
 * @param structuredOutput - Raw output from LLM (snake_case)
 * @returns Метрики для хранения. Пропускаются только null и нечисловые
 *   значения («не оценивали»); настоящий ноль сохраняется — по нему считается
 *   зона роста и строится коучинг.
 *
 * @example
 * const llmResult = await llmProvider.analyze(input);
 * const metrics = extractAnalysisMetrics(llmResult.data.structured_output);
 * await d1.saveMetrics(recordId, metrics);
 */
export function extractAnalysisMetrics(
  structuredOutput: Record<string, unknown>
): AnalysisMetrics {
  if (!structuredOutput) {
    throw new Error(
      `No structured output from LLM: received ${typeof structuredOutput} (${structuredOutput})`
    );
  }

  const metrics: AnalysisMetrics = {};

  // Helper: get value by snake_case or camelCase key
  const get = (snake: string, camel: string): unknown =>
    structuredOutput[snake] ?? structuredOutput[camel];

  // PROMPT_BUILDER_SCHEMA format (supports both snake_case and camelCase from LLM)
  // Only store aggregate metrics when the LLM returned a real number. The schema
  // forces these to null for non-applicable cases (e.g. sale_probability for any
  // non-sales category, or a disabled toggle), and a bare `Number(null)` === 0 would be
  // indistinguishable from a genuine 0 — polluting AVG()s and firing false
  // "Low score: 0/100" notifications. Absent → omit, so AVG() skips it.
  const qualityScore = asRealNumber(get('quality_score', 'qualityScore'));
  if (qualityScore != null) {
    metrics.qualityScore = qualityScore;
  }

  // Stored as percent 0-100, to hundredths: the model answers a fraction
  // (saleProbabilityFraction), and a bare `* 100` stores 0.55 as
  // 55.00000000000001 — verbatim into the CRM field and the public API.
  const saleFraction = saleProbabilityFraction(
    get('sale_probability', 'saleProbability')
  );
  if (saleFraction != null) {
    metrics.saleProbability = Math.round(saleFraction * 10000) / 100;
  }

  // Dialogue technique scores (only add if present)
  const greetingIntro = get('greeting_intro', 'greetingIntro');
  if (greetingIntro) {
    // asSubMetric (через asRealNumber), а не голое приведение: `Number(null) === 0` записало бы
    // «не оценивал» как «ноль баллов» — то же умолчание, которое needs и
    // closing уже не делают. Ноль как ОЦЕНКА при этом сохраняется: после #467
    // он попадает в зоны роста и коучинг.
    const greetingScore = asSubMetric((greetingIntro as { score?: unknown }).score);
    if (greetingScore != null) metrics.greetingScore = greetingScore;
  }
  const needsQualification = get('needs_qualification', 'needsQualification');
  if (needsQualification) {
    // null score → no rubric component applied to this call (consumer renewing
    // at a published price), so qualification was never this call's job. Omit
    // needsScore entirely — the same null-omission closingScore below relies
    // on — because a bare numeric coercion of null (=== 0) would store the exact
    // phantom 0 the prompt's "return score=null rather than 0" exists to stop,
    // dragging the canonical quality mean and firing "Low score: 0/100".
    const needsScore = asSubMetric((needsQualification as { score?: unknown }).score);
    if (needsScore != null) {
      metrics.needsScore = needsScore;
    }
  }
  const objectionHandling = get('objection_handling', 'objectionHandling');
  if (objectionHandling) {
    const objectionScore = asSubMetric((objectionHandling as { score?: unknown }).score);
    if (objectionScore != null) metrics.objectionScore = objectionScore;
  }
  const closingTechniques = get('closing_techniques', 'closingTechniques');
  if (closingTechniques) {
    // null score → closing was never applicable to this call (stale/wrong
    // contact, customer disengaged during qualification, disconnected
    // early). Omit closingScore entirely so the canonical quality-score mean
    // (QUALITY_SUB_METRIC_KEYS) and every AVG() over analyses.metrics skip
    // it, never read it as 0 — the same null-omission crmCompleteness below
    // already relies on. A bare numeric coercion of null (=== 0) would
    // silently reintroduce the exact "phantom 0" bug this field exists to
    // fix.
    const rawScore = (closingTechniques as { score?: unknown }).score;
    if (isCountedSubMetric(rawScore)) {
      metrics.closingScore = rawScore;
    }
  }

  // Emotional scores
  const politeness = get('politeness', 'politeness');
  if (politeness) {
    const politenessScore = asSubMetric((politeness as { score?: unknown }).score);
    if (politenessScore != null) metrics.politenessScore = politenessScore;
  }
  const clarity = get('clarity', 'clarity');
  if (clarity) {
    const clarityScore = asSubMetric((clarity as { score?: unknown }).score);
    if (clarityScore != null) metrics.clarityScore = clarityScore;
  }

  // Compliance scores
  const crmCompleteness = get('crm_completeness', 'crmCompleteness');
  if (crmCompleteness) {
    const applicable = (crmCompleteness as { applicable?: boolean }).applicable;
    const rawScore = (crmCompleteness as { score?: unknown }).score;
    // N/A (applicable:false) or null score → omit crmScore entirely so the
    // canonical mean skips it, never reads it as 0. Not a rated sub-metric, but
    // stored under the same limit (`isCountedSubMetric`): the CRM field, the
    // email and the webhook read it as stored, and a number beyond
    // QUALITY_SUB_METRIC_LIMIT is no CRM-completeness a model could mean (#2539).
    // A number only, as before — unlike `asSubMetric`, a numeric string is not
    // parsed: the call card reads the same field with the same guard.
    if (applicable !== false && isCountedSubMetric(rawScore)) {
      metrics.crmScore = rawScore;
    }
  }

  return metrics;
}

/**
 * Transform analysis output from snake_case to camelCase
 *
 * Use this at API boundary when returning analysis to frontend.
 *
 * @param structuredOutput - Raw output from LLM (snake_case)
 * @returns Transformed output (camelCase)
 *
 * @example
 * const llmResult = await llmProvider.analyze(input);
 * const camelCaseOutput = transformAnalysisOutput(llmResult.data.structured_output);
 * return c.json({ data: { structuredOutput: camelCaseOutput } });
 */
export function transformAnalysisOutput(
  structuredOutput: Record<string, unknown>
): AnalysisOutputCamelCase {
  return transformKeysToCamel(structuredOutput) as AnalysisOutputCamelCase;
}

/**
 * Process analysis output for API response
 *
 * Convenience function that combines metrics extraction and camelCase transformation.
 *
 * @param structuredOutput - Raw output from LLM (snake_case)
 * @returns Object with both metrics (for storage) and transformed output (for API)
 *
 * @example
 * const llmResult = await llmProvider.analyze(input);
 * const { metrics, output } = processAnalysisOutput(llmResult.data.structured_output);
 * await d1.saveMetrics(recordId, metrics);
 * return c.json({ data: { structuredOutput: output } });
 */
export function processAnalysisOutput(structuredOutput: Record<string, unknown>): {
  metrics: AnalysisMetrics;
  output: AnalysisOutputCamelCase;
} {
  const reconciled = reconcileObjectionHandling(
    transformAnalysisOutput(structuredOutput) as Record<string, unknown>
  ).output as AnalysisOutputCamelCase;
  return {
    metrics: extractAnalysisMetrics(reconciled),
    output: reconciled,
  };
}

/** A key moment as the analysis panel shows it. */
export interface XMLKeyMoment {
  timestamp?: string | null;
  label: string;
  quote: string;
  importance?: 'critical' | 'high' | 'medium' | 'low';
  speaker?: 'agent' | 'customer' | 'unknown';
}

type OutputKeyMoment = NonNullable<AnalysisOutputCamelCase['keyMoments']>[number];

function toXMLKeyMoment(m: OutputKeyMoment): XMLKeyMoment {
  return {
    timestamp: m.timestamp ?? null,
    label: m.label,
    quote: m.quote,
    importance: m.importance ?? undefined,
    speaker: m.speaker ?? undefined,
  };
}

/**
 * The key moment a raised attention flag stands on (#2002): the flag is raised
 * and exactly one moment carries `supportsAttentionFlag` — the mark the
 * finalizer leaves only beside the reason that moment explains (#1993). No
 * flag, no mark, or several marks — `null`: nothing says which moment it is.
 */
export function attentionFlagMoment(
  output: AnalysisOutputCamelCase
): XMLKeyMoment | null {
  if (output.attentionFlag?.triggered !== true || !Array.isArray(output.keyMoments)) {
    return null;
  }
  const marked = output.keyMoments.filter(
    (m) => typeof m === 'object' && m !== null && m.supportsAttentionFlag === true
  );
  return marked.length === 1 ? toXMLKeyMoment(marked[0]!) : null;
}

/**
 * XMLAnalysisResult format (hierarchical structure for AnalysisPanel)
 *
 * This matches the XMLAnalysisResult interface in AnalysisPanel.tsx
 * Used by UI to display analysis in a structured, categorized view.
 */
export interface XMLAnalysisResultCore {
  meta?: {
    analysisVersion?: string;
    confidenceLevel?: 'high' | 'medium' | 'low';
  };
  /**
   * Who each diarization label turned out to be, as resolved by the pipeline.
   *
   * The panel cannot re-derive this from the label: a rejected cleanup leaves
   * the protected `asr_source` transcript in place, whose labels are raw
   * `speaker_0`/`speaker_1`. Absent on analyses produced before the field
   * existed — consumers fall back to matching the record's agent name.
   */
  speakerRoles?: Record<string, SpeakerRole>;
  summary?: {
    qualityScore?: number;
    saleProbability?: number;
    attentionFlag?: boolean;
    attentionReason?: string;
    /** The key moment the raised flag stands on (`attentionFlagMoment`, #2002). */
    attentionMoment?: XMLKeyMoment;
  };
  // Text summary (separate from summary scores object)
  textSummary?: string;
  // Key moments from the call/chat
  keyMoments?: XMLKeyMoment[];
  // Structured summary (situation/actions/outcome)
  structuredSummary?: {
    situation?: string;
    actions?: string[];
    outcome?: string;
  };
  dialogue?: {
    greetingIntro?: {
      /** Absent with the score (beyond QUALITY_SUB_METRIC_LIMIT, #2525). */
      used?: boolean;
      score?: number;
      feedback?: string;
    };
    needsQualification?: {
      totalScore?: number;
      feedback?: string;
    };
    objectionHandling?: {
      objections?: Array<{
        type: string;
        handled: boolean;
        /** Absent on historical objection records. */
        score?: number;
        quote: string;
      }>;
      averageScore?: number;
    };
    closingTechnique?: {
      // null when closing was never applicable to this call — not a failed
      // close, which scores 0.
      score?: number | null;
      feedback?: string;
    };
  };
  content?: {
    topic?: {
      mainTopic?: string;
      keywords?: string[];
    };
  };
  emotional?: {
    sentiment?: {
      // Numeric client sentiment values (-1.0 to +1.0). Agent emotion not scored.
      clientStart?: number;
      clientEnd?: number;
      delta?: number;
      overall?: number;
    };
    politeness?: {
      score: number;
    };
    interruptions?: {
      count: number;
      byAgent: number;
      byClient: number;
      score: number;
    };
    clarity?: {
      /** Absent beyond QUALITY_SUB_METRIC_LIMIT; the issues stay (#2525). */
      score?: number;
      issues?: string[];
    };
  };
  compliance?: {
    recordingDisclaimer?: {
      applicable?: boolean;
      passed: boolean | null;
      timestamp?: string;
      quote?: string;
    };
    tcpaCompliance?: {
      applicable?: boolean;
      passed: boolean | null;
      reason?: string;
      checklist?: {
        companyIdentified?: boolean;
        purposeStated?: boolean;
        optOutOffered?: boolean;
      } | null;
    };
    dataPrivacy?: {
      applicable?: boolean;
      passed: boolean | null;
    };
    crmCompleteness?: {
      passed: boolean;
      missingFields?: string[];
      score?: number;
    };
  };
  speech?: {
    fillerWords?: {
      count: number;
      /**
       * `count` отсутствует: хранятся только самые частые формы, без числа
       * каждой. Прежнее `count: 1` на каждый пример было выдумкой, и экран
       * показывал «е-е-е ×1» при двадцати «е-е-е» (#819, ревью).
       */
      topWords: Array<{ word: string; count?: number }>;
      score: number;
    };
    stopWords?: {
      count: number;
      topWords: Array<{ word: string; count: number }>;
      score: number;
    };
    mandatoryPhrases?: {
      used: string[];
      missing: string[];
      // Stable codes for `missing` (analysis-metric-semantics, Task 3). Absent
      // on analyses stored before this field existed — a consumer must not
      // read an absent/empty missingCodes as "nothing missing" on its own;
      // fall back to `missing` for older analyses. Whether mandatory phrases
      // were checked at all is signalled one level up: this whole
      // `mandatoryPhrases` object is present only when the toggle was on for
      // that analysis — absent means "not checked", not "nothing missing".
      missingCodes?: string[];
      score: number;
    };
    profanityDetection?: {
      detected: boolean;
      /** Grouped by phrase+speaker so the UI renders chips, not a pre-joined string. */
      instances?: Array<{
        phrase: string;
        speaker: 'agent' | 'client' | 'unknown';
        count: number;
      }>;
    };
    operatorErrors?: {
      count: number;
      errors?: string[];
    };
  };
  recommendations?: {
    strengths?: string[];
    improvements?: Array<{
      category?: string;
      criterionKey: string;
      advice: string;
      priority?: 'low' | 'medium' | 'high';
    }>;
  };
  businessIntelligence?: {
    competitorMentions?: Array<{
      name: string;
      context: string;
      sentiment: 'positive' | 'neutral' | 'negative';
    }>;
    productFeedback?: Array<{
      product: string;
      feedback: string;
      severity: 'praise' | 'minor_issue' | 'major_issue' | 'incident';
    }>;
    marketSignals?: Array<{
      signal: string;
      type: 'pricing' | 'supply' | 'regulation' | 'trend' | 'demand';
    }>;
    complianceFlags?: Array<{
      flag: string;
      context: string;
    }>;
  };
  // Follow-up commitments made during the call (callbacks, meetings, tasks).
  // Surfaced in the analysis view so the user can see what was detected,
  // independent of whether CRM activity creation is enabled.
  agreements?: Array<{
    type: 'call' | 'meeting' | 'email' | 'task';
    subject: string;
    description?: string;
    dueHint?: string;
    dueAt?: string;
    responsibleHint?: string;
    confidence?: number;
    evidenceQuote?: string;
  }>;
}

/**
 * Normalize strength item to string for UI display
 *
 * Expected format from LLM (per PROMPT_BUILDER_SCHEMA):
 * - { behavior: "...", impact: "..." }
 *
 * Legacy/fallback formats:
 * - Plain string: "Good greeting technique"
 *
 * Output: "behavior — impact" or just the string value
 */
function normalizeStrength(item: unknown): string {
  if (typeof item === 'string') {
    return item;
  }
  if (typeof item === 'object' && item !== null) {
    const obj = item as Record<string, unknown>;
    const behavior = typeof obj.behavior === 'string' ? obj.behavior : null;
    const impact = typeof obj.impact === 'string' ? obj.impact : null;

    // Combine behavior + impact for rich display. The dash continues the
    // sentence, so the model's own stop before it goes: «привітався. — …»
    // read as two sentences glued by a dash (#1110, code review).
    if (behavior && impact) {
      return `${withoutFinalStop(behavior)} — ${impact}`;
    }
    if (behavior) return behavior;
    if (impact) return impact;

    // Fallback: find first string value
    for (const value of Object.values(obj)) {
      if (typeof value === 'string') return value;
    }
    return JSON.stringify(item);
  }
  return String(item);
}

/**
 * A recommendation as every surface shows it — the analysis panel, the PDF and
 * the CRM report — from whatever shape the model returned (#1423):
 *
 * - `{ behavior, suggestion }` (PROMPT_BUILDER_SCHEMA): the lead and the advice;
 * - `{ behavior, advice }`, and a plain string — the legacy forms;
 * - an object with only `behavior`: the lead alone, not the lead repeated as
 *   its own advice;
 * - an object with neither, but some other text field: that text as the advice.
 *
 * Joined by `recommendationParts`. `null` for what carries no text at all —
 * `42`, `null`, `{}`, an empty string: such an item is dropped rather than
 * shown as «null» or posted to a CRM as an empty point.
 */
export function improvementParts(item: unknown): { lead: string; advice: string } | null {
  if (typeof item === 'string') {
    return item.trim() ? recommendationParts('', item) : null;
  }
  if (typeof item !== 'object' || item === null) return null;

  const obj = item as Record<string, unknown>;
  const text = (key: string) =>
    typeof obj[key] === 'string' ? (obj[key] as string) : '';
  const lead = text('behavior');
  let advice = text('suggestion') || text('advice');
  if (!lead.trim() && !advice.trim()) {
    const other = Object.values(obj).find(
      (value): value is string => typeof value === 'string' && value.trim() !== ''
    );
    advice = other ?? '';
  }
  if (!lead.trim() && !advice.trim()) return null;
  return recommendationParts(lead, advice);
}

/**
 * Normalize improvement item to XMLAnalysisResultCore format — the panel and
 * the PDF read `criterionKey` as the lead (empty when there is none) and
 * `advice` as the advice. Output: { criterionKey, advice, priority }.
 */
function normalizeImprovement(item: unknown): {
  criterionKey: string;
  advice: string;
  priority: 'low' | 'medium' | 'high';
} | null {
  const parts = improvementParts(item);
  if (!parts) return null;
  return {
    criterionKey: parts.lead,
    advice: parts.advice,
    priority: 'medium' as const,
  };
}

/**
 * Group raw profanity instances by phrase+speaker, counting repeats.
 *
 * The LLM emits one entry per occurrence (with a timestamp we don't need
 * here); the UI wants one chip per distinct phrase+speaker with a count,
 * not a pre-joined "phrase (speaker)" string per occurrence.
 */
function groupProfanityInstances(
  instances: Array<{
    speaker: 'agent' | 'client' | 'unknown';
    phrase: string;
    timestamp: string;
  }>
): Array<{ phrase: string; speaker: 'agent' | 'client' | 'unknown'; count: number }> {
  const grouped = new Map<
    string,
    { phrase: string; speaker: 'agent' | 'client' | 'unknown'; count: number }
  >();

  for (const inst of instances) {
    const key = `${inst.phrase}\u0000${inst.speaker}`;
    const existing = grouped.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      grouped.set(key, { phrase: inst.phrase, speaker: inst.speaker, count: 1 });
    }
  }

  return Array.from(grouped.values());
}

/**
 * Transform flat PROMPT_BUILDER_SCHEMA output to XMLAnalysisResult format
 *
 * DRY: Use this in UI mappers to convert analysis output for AnalysisPanel.
 *
 * @param output - Flat camelCase output from API (AnalysisOutputCamelCase)
 * @returns Hierarchical XMLAnalysisResult for AnalysisPanel
 *
 * @example
 * // In analyses.mapper.ts
 * const xmlResult = toXMLAnalysisResult(apiAnalysis.fullResult.structuredOutput);
 * return { ...data, result: xmlResult };
 */
export function toXMLAnalysisResult(
  output: AnalysisOutputCamelCase
): XMLAnalysisResultCore {
  // Map sentiment string to number (-1.0 to +1.0 scale)
  // Legacy format used 0-1 scale, new format uses -1.0 to +1.0
  const sentimentToNumber = (sentiment?: string | number): number | undefined => {
    if (sentiment === undefined || sentiment === null) return undefined;
    if (typeof sentiment === 'number') return sentiment;
    // Legacy text format → convert to -1.0 to +1.0 scale
    if (sentiment === 'positive') return 0.6;
    if (sentiment === 'neutral') return 0.0;
    if (sentiment === 'negative') return -0.6;
    return undefined;
  };

  // A section score is read by the writer's rule (`asSubMetric`): what the
  // quality score skips — beyond QUALITY_SUB_METRIC_LIMIT (#1889), not a number
  // — is no data here either. Copied through, it would print as `1e+305` with a
  // bar `1e305%` wide in the panel and in the print report, which cannot be
  // corrected once exported (#2525). Only the number goes: a section that is
  // nothing but the score goes with it, one that carries more (feedback,
  // issues, the objections list) keeps the rest.
  const sectionScore = (score: unknown): number | undefined =>
    asSubMetric(score) ?? undefined;

  const result: XMLAnalysisResultCore = {};

  // Speaker roles — lifted out of the metadata block so the panel can attribute
  // talk time to the right side without guessing from the diarization label.
  const speakerRoles = output.analysisMetadata?.speakerRoles;
  if (speakerRoles && Object.keys(speakerRoles).length > 0) {
    result.speakerRoles = speakerRoles;
  }

  // Summary section. The quality score is the canonical sub-metric mean — the
  // number the records list, its filter and the dashboard show — not the
  // model's own `qualityScore`, which older analyses report differently
  // (#1203). No sub-metric → no score, exactly as the list shows none.
  const qualityScore =
    computeCanonicalQualityScore(
      extractAnalysisMetrics(output as unknown as Record<string, unknown>)
    ) ?? undefined;
  if (
    qualityScore !== undefined ||
    output.saleProbability !== undefined ||
    output.attentionFlag
  ) {
    result.summary = {
      qualityScore,
      saleProbability: output.saleProbability ?? undefined,
      attentionFlag: output.attentionFlag?.triggered,
      attentionReason: output.attentionFlag?.reason,
    };
    const attentionMoment = attentionFlagMoment(output);
    if (attentionMoment) result.summary.attentionMoment = attentionMoment;
  }

  // Dialogue section
  const dialogue: NonNullable<XMLAnalysisResultCore['dialogue']> = {};

  const greetingIntro = output.greetingIntro;
  const greetingScore = sectionScore(greetingIntro?.score);
  if (greetingScore !== undefined) {
    dialogue.greetingIntro = {
      used: greetingScore > 50,
      score: greetingScore,
      feedback: greetingIntro?.feedback,
    };
  } else if (greetingIntro?.feedback) {
    // No score, no verdict on "used" either: it is read off the score.
    dialogue.greetingIntro = { feedback: greetingIntro.feedback };
  }

  // null (qualification not applicable to this call) is absent like any other
  // non-score: the section carries nothing else.
  const needsScore = sectionScore(output.needsQualification?.score);
  if (needsScore !== undefined) {
    dialogue.needsQualification = { totalScore: needsScore };
  }

  if (output.objectionHandling) {
    // The declared type promises `objections` is always an array; the stored
    // data disagrees. Analyses persisted before 2026-07-14 kept
    // objection_handling as { score, objections_handled: string[] } — no
    // `objections` key at all — and even older rows kept it as a bare array.
    // Both still pass isPromptBuilderFormat (it only looks for qualityScore /
    // needsQualification), both are still in the DB and will never be
    // re-analysed, and an unguarded .map() threw a TypeError that took the
    // whole analysis panel and the print report down with it.
    const objections = output.objectionHandling.objections;
    const averageScore = sectionScore(output.objectionHandling.score);
    const items = Array.isArray(objections)
      ? objections.map((objection) => ({
          type: objection.type,
          handled: objection.handled,
          // An item's score by the product's one rule for it (#846), the one
          // the "handled" verdict beside it reads (`isObjectionHandled`). The
          // key only where the item had one: its absence is what lets a
          // historical item's `handled` flag stand.
          ...(Object.prototype.hasOwnProperty.call(objection, 'score')
            ? { score: readObjectionScore(objection) ?? undefined }
            : {}),
          quote: objection.quote,
        }))
      : [];
    if (averageScore !== undefined || items.length > 0) {
      dialogue.objectionHandling = { averageScore, objections: items };
    }
  }

  const closingScore = output.closingTechniques?.score;
  if (closingScore === null) {
    // null (closing not applicable) stays null — not the same as no data.
    dialogue.closingTechnique = { score: null };
  } else if (isCountedSubMetric(closingScore)) {
    // The close is stored for a real number only (`extractAnalysisMetrics`
    // does not parse a numeric string here, unlike `asSubMetric`), so the
    // section shows exactly what the stored metrics and the overall score
    // hold (#2803).
    dialogue.closingTechnique = { score: closingScore };
  }

  if (Object.keys(dialogue).length > 0) result.dialogue = dialogue;

  // Content section
  if (output.topic || output.keywords) {
    result.content = {
      topic: {
        mainTopic: output.topic ?? undefined,
        keywords: output.keywords ?? undefined,
      },
    };
  }

  // Emotional section
  if (output.sentiment || output.politeness || output.interruptions || output.clarity) {
    const emotional: NonNullable<XMLAnalysisResultCore['emotional']> = {};

    if (output.sentiment) {
      // Handle both numeric (new) and text (legacy) sentiment formats
      const s = output.sentiment;

      // Check if numeric format (new XML schema). Client sentiment only —
      // agent (employee) emotion is no longer scored (EU AI Act Art. 5(1)(f)).
      const hasNumericFormat =
        typeof s.clientStart === 'number' || typeof s.clientEnd === 'number';

      if (hasNumericFormat) {
        // New numeric format - pass through directly
        emotional.sentiment = {
          clientStart: s.clientStart,
          clientEnd: s.clientEnd,
          delta: s.delta,
          overall:
            typeof s.overall === 'number' ? s.overall : sentimentToNumber(s.overall),
        };
      } else {
        // Legacy text format - convert to numbers (client only)
        const customerVal = sentimentToNumber(s.customer);
        const overallVal =
          typeof s.overall === 'number' ? s.overall : sentimentToNumber(s.overall);

        emotional.sentiment = {
          clientStart: customerVal,
          clientEnd: customerVal,
          delta: 0,
          overall: overallVal,
        };
      }
    }

    const politenessScore = sectionScore(output.politeness?.score);
    if (politenessScore !== undefined) {
      emotional.politeness = { score: politenessScore };
    }

    if (output.interruptions) {
      emotional.interruptions = {
        count: output.interruptions.count,
        byAgent: output.interruptions.byAgent,
        byClient: output.interruptions.byCustomer,
        score: Math.max(0, 100 - output.interruptions.count * 10), // Derive score
      };
    }

    const clarityScore = sectionScore(output.clarity?.score);
    const clarityFeedback = output.clarity?.feedback;
    if (clarityScore !== undefined || clarityFeedback) {
      emotional.clarity = {
        score: clarityScore,
        issues: clarityFeedback ? [clarityFeedback] : undefined,
      };
    }

    if (Object.keys(emotional).length > 0) result.emotional = emotional;
  }

  // Compliance section
  //
  // Only analyses stored before stage E3 (#2846) carry it.
  // output.compliance (applyComplianceApplicability's output, camelCased) is
  // the source of truth for recordingDisclaimer/tcpaCompliance/dataPrivacy
  // pass/fail — it already carries the applicable gate and the null-when-
  // not-applicable verdict, so those are read through, not recomputed. The
  // legacy top-level `recordingDisclaimer.mentioned` field is used only for
  // (a) the disclosure timestamp, which compliance.recordingDisclaimer does
  // not carry, and (b) as the sole fallback for analyses stored before the
  // `compliance` block existed.
  if (output.compliance || output.recordingDisclaimer || output.crmCompleteness) {
    result.compliance = {};

    const compliance = output.compliance;
    if (compliance?.recordingDisclaimer) {
      result.compliance.recordingDisclaimer = {
        applicable: compliance.recordingDisclaimer.applicable,
        passed: compliance.recordingDisclaimer.passed,
        quote: compliance.recordingDisclaimer.quote || undefined,
        timestamp: output.recordingDisclaimer?.timestamp || undefined,
      };
    } else if (output.recordingDisclaimer) {
      result.compliance.recordingDisclaimer = {
        passed: output.recordingDisclaimer.mentioned,
        timestamp: output.recordingDisclaimer.timestamp || undefined,
      };
    }

    if (compliance?.tcpaCompliance) {
      result.compliance.tcpaCompliance = {
        applicable: compliance.tcpaCompliance.applicable,
        passed: compliance.tcpaCompliance.passed,
        reason: compliance.tcpaCompliance.reason || undefined,
        checklist: compliance.tcpaCompliance.checklist ?? null,
      };
    }

    if (compliance?.dataPrivacy) {
      result.compliance.dataPrivacy = {
        applicable: compliance.dataPrivacy.applicable,
        passed: compliance.dataPrivacy.passed,
      };
    }

    // The writer's rule for `crmScore` (isCountedSubMetric, #2539): a score it
    // does not store is no data on the call card either, not a passing 1e+305.
    const crm = output.crmCompleteness;
    if (crm && crm.applicable !== false && isCountedSubMetric(crm.score)) {
      result.compliance.crmCompleteness = {
        passed: crm.score >= 80,
        missingFields: crm.missingFields,
        score: crm.score,
      };
    }
  }

  // Speech section
  if (
    output.fillerWords ||
    output.stopWords ||
    output.mandatoryPhrases ||
    output.profanityDetection ||
    output.operatorErrors
  ) {
    result.speech = {};

    if (output.fillerWords) {
      const { count, examples, perMinute } = output.fillerWords;
      result.speech.fillerWords = {
        count,
        topWords: examples.map((word) => ({ word })),
        // Балл — от частоты, а не от абсолютного числа: с настоящим счётом
        // (#819) пятнадцатиминутный звонок получал бы ноль за ту же манеру,
        // за которую двухминутный получает 90. Шкала — та, что стояла в
        // промпте: до 5 в минуту приемлемо, 10 и больше — проблема.
        score:
          typeof perMinute === 'number'
            ? Math.max(0, Math.round(100 - perMinute * 10))
            : Math.max(0, 100 - count * 5),
      };
    }

    if (output.stopWords) {
      // Count only what the AGENT said — this is the agent-hedging metric.
      // Since #981 a stop word's speaker comes from diarization, and a quote
      // both sides said (or nobody said) is `unknown`; counting that against
      // the agent put exactly the ambiguous hits back on the agent's score
      // (Codex P1, #994). Legacy plain strings predate speakers and count.
      const stopTokens = output.stopWords.detected
        .filter((d) => typeof d === 'string' || d.speaker === 'agent')
        .map((d) => (typeof d === 'string' ? d : d.token));
      result.speech.stopWords = {
        count: stopTokens.length,
        topWords: stopTokens.map((word) => ({ word, count: 1 })),
        score: Math.max(0, 100 - stopTokens.length * 10), // Derive score
      };
    }

    if (output.mandatoryPhrases) {
      const total = output.mandatoryPhrases.required.length || 1;
      const found = output.mandatoryPhrases.found.length;
      result.speech.mandatoryPhrases = {
        used: output.mandatoryPhrases.found,
        missing: output.mandatoryPhrases.missing,
        // Older stored analyses have no missing_codes key at all; leave
        // missingCodes undefined rather than defaulting to [] so a consumer
        // can tell "no codes recorded" apart from "checked, none missing".
        missingCodes: output.mandatoryPhrases.missingCodes,
        score: Math.round((found / total) * 100),
      };
    }

    if (output.profanityDetection) {
      result.speech.profanityDetection = {
        detected: output.profanityDetection.detected,
        instances: groupProfanityInstances(output.profanityDetection.instances),
      };
    }

    if (output.operatorErrors) {
      result.speech.operatorErrors = {
        count: output.operatorErrors.length,
        errors: output.operatorErrors.map((e) => `${e.type}: ${e.description}`),
      };
    }
  }

  // Recommendations section
  // LLM returns objects per PROMPT_BUILDER_SCHEMA:
  // - strengths: [{ behavior, impact }]
  // - improvements: [{ behavior, suggestion }]
  // normalizeStrength/normalizeImprovement convert to UI-friendly strings
  if (output.strengthsImprovements) {
    const strengths = output.strengthsImprovements.strengths;
    const improvements = output.strengthsImprovements.improvements;

    result.recommendations = {
      strengths: Array.isArray(strengths) ? strengths.map(normalizeStrength) : [],
      improvements: Array.isArray(improvements)
        ? improvements.flatMap((item) => normalizeImprovement(item) ?? [])
        : [],
    };
  }

  // Text summary (separate from qualityScore/saleProbability object)
  if (output.summary) {
    result.textSummary = output.summary;
  }

  // Key moments from the call/chat
  if (output.keyMoments && Array.isArray(output.keyMoments)) {
    result.keyMoments = output.keyMoments.map(toXMLKeyMoment);
  }

  // Structured summary (situation/actions/outcome)
  if (output.structuredSummary) {
    result.structuredSummary = {
      situation: output.structuredSummary.situation ?? undefined,
      actions: output.structuredSummary.actions ?? undefined,
      outcome: output.structuredSummary.outcome ?? undefined,
    };
  }

  // Business intelligence
  if (output.businessIntelligence) {
    const bi = output.businessIntelligence;
    const hasAny =
      (bi.competitorMentions && bi.competitorMentions.length > 0) ||
      (bi.productFeedback && bi.productFeedback.length > 0) ||
      (bi.marketSignals && bi.marketSignals.length > 0) ||
      (bi.complianceFlags && bi.complianceFlags.length > 0);
    if (hasAny) {
      result.businessIntelligence = {
        competitorMentions: bi.competitorMentions,
        productFeedback: bi.productFeedback,
        marketSignals: bi.marketSignals,
        complianceFlags: bi.complianceFlags,
      };
    }
  }

  // Follow-up agreements (callbacks, meetings, tasks). Shown verbatim so the
  // user can verify what the LLM detected — CRM activity creation is a separate
  // delivery action and is NOT a prerequisite for displaying them here.
  if (output.agreements && Array.isArray(output.agreements)) {
    const agreements = output.agreements
      .filter((a) => a && a.subject?.trim())
      .map((a) => ({
        type: a.type,
        subject: a.subject,
        description: a.description || undefined,
        dueHint: a.dueHint || undefined,
        dueAt: a.dueAt || undefined,
        responsibleHint: a.responsibleHint || undefined,
        confidence: a.confidence ?? undefined,
        evidenceQuote: a.evidenceQuote || undefined,
      }));
    if (agreements.length > 0) {
      result.agreements = agreements;
    }
  }

  return result;
}
